'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';

export default function DurationRiskPage() {
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}
    >
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-6 md:px-12 lg:px-20"
        style={{
          height: '72px',
          backgroundColor: 'rgba(13,26,58,0.95)',
          borderBottom: '1px solid rgba(30,48,104,0.6)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Link
          href="/#insights"
          className="flex items-center gap-2 text-sm font-medium"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <ArrowLeft size={15} />
          Back to Perspectives
        </Link>
        <div className="flex items-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/assets/macro-capital-logo.svg" alt="Macro Capital" style={{ width: '36px', height: '36px' }} />
          <span className="text-sm font-bold tracking-widest uppercase" style={{ color: 'var(--foreground)' }}>
            Macro Capital
          </span>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <span
            className="text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full"
            style={{
              backgroundColor: 'rgba(20,184,166,0.08)',
              border: '1px solid rgba(20,184,166,0.2)',
              color: '#2dd4bf',
            }}
          >
            Fixed Income
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Calendar size={11} /> November 2024
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Clock size={11} /> 5 min read
          </span>
        </div>

        <h1
          className="font-semibold leading-tight mb-6"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            color: 'var(--foreground)',
          }}
        >
          Duration Risk in a Higher-for-Longer Regime
        </h1>

        <p className="text-lg leading-relaxed mb-10" style={{ color: 'var(--muted-foreground)' }}>
          The repricing of long-duration assets is not yet complete. We assess where the risk-reward sits across the yield curve today — and how investors should position their fixed income allocations for the current regime.
        </p>

        <div className="h-px mb-10" style={{ backgroundColor: 'var(--border)' }} />

        <div className="space-y-8" style={{ color: 'var(--muted-foreground)', lineHeight: '1.85' }}>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Understanding Duration Risk
            </h2>
            <p className="mb-4">
              Duration is the measure of a bond's sensitivity to changes in interest rates. A bond with a duration of 10 years will fall approximately 10% in price for every 1 percentage point rise in yields. This relationship is mechanical and unavoidable — it is the fundamental arithmetic of discounted cash flows.
            </p>
            <p className="mb-4">
              For most of the period from 2009 to 2021, duration risk was rewarded rather than punished. Interest rates were falling, which meant bond prices were rising, and investors who held long-duration bonds earned both the coupon and capital appreciation. The 40-year bull market in bonds — which began when Volcker broke inflation in the early 1980s — created a generation of investors who had never experienced a sustained period of rising rates.
            </p>
            <p>
              The 2022 rate shock was a brutal reminder that duration risk is real. The Bloomberg US Aggregate Bond Index fell more than 15% in 2022 — its worst calendar year performance in modern history. Long-duration government bonds fell 30–40%. Investors who had loaded up on duration in search of yield in a low-rate environment suffered losses that, in some cases, exceeded those of equity portfolios.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              The Higher-for-Longer Debate
            </h2>
            <p className="mb-4">
              The "higher-for-longer" narrative — the view that interest rates will remain elevated relative to the post-2008 norm for an extended period — has been one of the dominant themes in fixed income markets since 2022. The argument rests on several structural factors: sticky services inflation, a tight labour market, fiscal expansion that keeps demand elevated, and a potential structural shift in the neutral rate of interest.
            </p>
            <p className="mb-4">
              The neutral rate — the interest rate that is neither stimulative nor restrictive — is unobservable and can only be estimated. For most of the 2010s, the consensus estimate was around 2.5% in nominal terms. But several economists, including former Fed officials, have argued that the neutral rate has risen to 3.0–3.5% or higher, driven by increased government borrowing, the energy transition, and deglobalisation trends that are inflationary at the margin.
            </p>
            <p>
              If the neutral rate is indeed higher than previously assumed, then the current level of interest rates — with the Fed funds rate in the 4.25–4.50% range as of early 2025 — is only modestly restrictive, not dramatically so. This would imply that the easing cycle will be shallower than markets currently expect, and that long-duration bonds are not yet as attractive as their current yields suggest.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Where the Risk-Reward Sits Today
            </h2>
            <p className="mb-4">
              The yield curve today offers a more nuanced picture than the simple "higher-for-longer" narrative suggests. At the short end, 2-year Treasury yields of approximately 4.0–4.5% offer attractive real returns with minimal duration risk. These yields are well above the expected inflation rate, providing a positive real return without the volatility of long-duration bonds.
            </p>
            <p className="mb-4">
              At the long end, 10-year and 30-year Treasury yields of 4.5–5.0% are historically attractive in absolute terms, but the duration risk is significant. A 1 percentage point rise in 10-year yields — which is not an implausible scenario if the neutral rate is higher than expected — would generate a capital loss of approximately 8–9% on a 10-year bond, more than offsetting a year's worth of coupon income.
            </p>
            <p>
              Our current positioning reflects a preference for the belly of the yield curve — 5-year maturities — where we believe the risk-reward is most attractive. Five-year yields offer a meaningful yield pickup over short-term rates, with less duration risk than the long end. As the easing cycle progresses and the yield curve normalises, we expect to gradually extend duration, but we are not yet at the point where the long end offers sufficient compensation for the risks involved.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
              Credit vs. Duration: The Current Trade-off
            </h2>
            <p className="mb-4">
              In the current environment, we believe credit risk offers a better risk-reward than duration risk for investors seeking to enhance yield above the risk-free rate. Investment-grade corporate bonds offer spreads of 80–100 basis points over Treasuries — not historically wide, but reasonable given the strong corporate balance sheets and low default rates that characterise the current credit environment.
            </p>
            <p className="mb-4">
              High-yield credit is more nuanced. Spreads have compressed significantly from their 2022 wides, and at current levels of 300–350 basis points over Treasuries, the compensation for default risk is modest. We prefer to access high-yield exposure through floating-rate instruments — leveraged loans and CLO tranches — which offer similar yields without the duration risk.
            </p>
            <p>
              The key risk to our fixed income positioning is a sharp deterioration in credit quality — a recession scenario in which corporate defaults rise significantly. This is not our base case, but it is a scenario that we hedge against through careful credit selection, a focus on investment-grade issuers, and maintaining adequate liquidity in the portfolio. In fixed income, as in equities, quality is the most reliable source of long-term outperformance.
            </p>
          </section>

        </div>

        <div className="mt-16 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
          <p className="text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>
            This article is for informational purposes only and does not constitute investment advice. Past performance is not indicative of future results. For qualified investors only.
          </p>
          <Link
            href="/#insights"
            className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: 'var(--primary)' }}
          >
            <ArrowLeft size={14} />
            Back to all perspectives
          </Link>
        </div>
      </article>
    </div>
  );
}
